import dayjs from "dayjs";
import { ChevronLeft, ChevronRight } from "lucide-react-native";
import { useCallback, useEffect, useMemo, useRef } from "react";
import DateTimePicker, {
  useDefaultClassNames,
} from "react-native-ui-datepicker";
import { Icon } from "../../ui/icon";
import { PickerWrapperProps } from "./types";

export default function DatePickerFormModal(props: PickerWrapperProps) {
  const originalDate = useMemo(() => props.date || dayjs().toDate(), [props]);
  const selectDate = useCallback(
    (date: Date) => {
      props.onConfirm(date);
      props.onBlur();
    },
    [props],
  );
  // react-native-ui-datepicker calls `onChange` from its own mount effect whenever a
  // `timeZone` is set (its previous-timezone ref starts undefined), which would
  // confirm a date nobody picked and close an inline calendar the moment it opens.
  // Child effects run before this one, so that call lands while `mounted` is false.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
  }, []);
  const defaultClassNames = useDefaultClassNames();
  return (
    <DateTimePicker
      mode="single"
      date={originalDate}
      disabledDates={
        props.minDate || props.maxDate
          ? (date) => {
              const day = dayjs(date);
              if (props.minDate && day.isBefore(props.minDate, "day")) {
                return true;
              }
              if (props.maxDate && day.isAfter(props.maxDate, "day")) {
                return true;
              }
              return false;
            }
          : undefined
      }
      onChange={(dates) => {
        if (mounted.current && dates.date) {
          const newDate =
            dates.date instanceof Date
              ? new Date(dates.date)
              : dates.date instanceof dayjs.Dayjs
                ? dates.date.toDate()
                : new Date(dates.date);
          selectDate(newDate);
        }
      }}
      firstDayOfWeek={1}
      showOutsideDays
      navigationPosition="right"
      containerHeight={250}
      classNames={{
        ...defaultClassNames,
        today: "", // Add a border to today's date
        day: "group hover:bg-brand-200 aspect-square rounded-full",
        day_cell: "p-1",
        days: "w-72",
        selected: "bg-brand-400 hover:bg-brand-400",
        disabled: "opacity-50",
        day_label: "font-body text-xs",
        month_label: "font-body text-xs capitalize",
        year_label: "font-body text-xs capitalize",
        month_selector_label:
          "font-body text-sm capitalize font-bold hover:underline",
        year_selector_label: "font-body text-sm font-bold hover:underline",
        time_selector_label: "font-body text-xs",
        weekday_label: "font-body text-xs capitalize",
        time_label: "font-body text-xs",
        range_end_label: "text-typography-50",
        range_middle_label: "font-body text-xs",
        range_start_label: "text-typography-50",
        selected_label: "text-typography-50", // Highlight the selected day label
        disabled_label: "font-body text-xs",
        outside_label: "font-body text-xs text-typography-200",
        today_label: "font-body text-xs",
        selected_month_label: "font-body",
        selected_year_label: "font-body text-sm",
        active_year_label: "font-body text-sm",
        button_prev: "hover:bg-primary-50",
        button_next: "hover:bg-primary-50",
      }}
      locale="es"
      timeZone="America/Santiago"
      components={{
        IconPrev: <Icon as={ChevronLeft} size="sm" />,
        IconNext: <Icon as={ChevronRight} size="sm" />,
      }}
    />
  );
}
